import {FormikProvider, useFormik} from 'formik';
import {useTranslation} from 'react-i18next';

import {Box, Grid, Typography} from '@mui/material';

import {
  surveySectionDescriptionStyles,
  surveySectionTitleStyles,
} from '../surveyQuestionnaireTypography';

import {DemographicQuestionsQuestions} from './DemographicQuestions';
import {EmpionButton} from '../../../../../../../components/EmpionButton/EmpionButton';
import {EmpionPaper} from '../../../../../../../components/EmpionPaper/EmpionPaper';
import {QuestionnaireSubmit} from '../../../../../../../hooks/useCompanyQuestionnaireUpdate';
import {greenColors} from '../../../../../../../layouts/theme/designSystem';
import {DemographicsRequestBody} from '../../../../../../../lib/api/RequestBodies/features/company/Questionnaires/QuestionnaireRequestBodies';
import {QuestionnaireSection} from '../../../../../../../types/CompanyQuestionnaire';
import {CompanySurveyQuestionnaire} from '../../../types/SurveyQuestionnaireTypes';

interface Props {
  onSubmit: QuestionnaireSubmit;
  demographicQuestions: CompanySurveyQuestionnaire['surveyDemographic'] | undefined;
}

export const QuestionnaireDemographic = ({demographicQuestions, onSubmit}: Props) => {
  const {t} = useTranslation();
  const demographicQuestionIds = demographicQuestions?.questions?.map((question) => question.id);
  const hasQuestion = (questionId: string) => !demographicQuestionIds || demographicQuestionIds.includes(questionId);

  const formik = useFormik({
    initialValues: {
      ageGroup: '',
      gender: '',
      companyDepartment: '',
      workMode: '',
      spokenLanguage: '',
      genderDiversityInLeadership: 3,
      companyLocation: '',
    },
    onSubmit: () => {
      const answers = {
        ...(hasQuestion('ageGroup') && {ageGroup: formik.values.ageGroup}),
        ...(hasQuestion('gender') && {gender: formik.values.gender}),
        ...(hasQuestion('companyDepartment') && {companyDepartment: formik.values.companyDepartment}),
        ...(hasQuestion('companyLocation') && {companyLocation: formik.values.companyLocation}),
        ...(hasQuestion('workMode') && {workMode: formik.values.workMode}),
        ...(hasQuestion('spokenLanguage') && {spokenLanguage: formik.values.spokenLanguage}),
        ...(hasQuestion('genderDiversityInLeadership') && {
          genderDiversityInLeadership: formik.values.genderDiversityInLeadership,
        }),
      } as DemographicsRequestBody['answers'];

      onSubmit(QuestionnaireSection.Demographics, {answers});
    },
    validate: (values) => {
      const errors: any = {};

      if (hasQuestion('ageGroup') && !values.ageGroup) {
        errors.ageGroup = t('shared.required', {
          fieldName: 'ageGroup',
        });
      }
      if (hasQuestion('gender') && !values.gender) {
        errors.gender = t('shared.required');
      }

      if (hasQuestion('companyDepartment') && !values.companyDepartment) {
        errors.companyDepartment = t('shared.required');
      }

      if (hasQuestion('companyLocation') && !values.companyLocation) {
        errors.companyLocation = t('shared.required');
      }

      if (hasQuestion('workMode') && !values.workMode) {
        errors.workMode = t('shared.required');
      }

      if (hasQuestion('spokenLanguage') && !values.spokenLanguage) {
        errors.spokenLanguage = t('shared.required');
      }

      if (hasQuestion('genderDiversityInLeadership') && !values.genderDiversityInLeadership) {
        errors.genderDiversityInLeadership = t('shared.required');
      }

      return errors;
    },
  });

  return (
    <Grid
      container
      sx={{
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant='h2' gutterBottom align='center' sx={surveySectionTitleStyles}>
          {t('companySurveys.public.demographic.generalQuestions')}
        </Typography>
        <Typography variant='body1' align='center' sx={surveySectionDescriptionStyles}>
          {t('companySurveys.public.demographic.description')}
        </Typography>
      </Box>
      <Grid
        item
        xs={12}
        sx={{
          display: {md: 'flex'},
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <DemographicQuestionsQuestions formik={formik} demographicQuestions={demographicQuestions} />
            <EmpionPaper
              sx={{
                backgroundColor: 'white',
                borderRadius: '16px',
                width: {xs: '100%', sm: '100%', md: '736px'},
                padding: '14px',
                marginTop: 2,
              }}
            >
              <Typography variant='body2' color={greenColors[700]}>
                {t('companySurveys.public.demographic.requiredDescription')}
              </Typography>
            </EmpionPaper>
            <EmpionButton
              sx={{
                mt: 3,
              }}
              type='submit'
            >
              {t('shared.formSubmit.submit')}
            </EmpionButton>
          </form>
        </FormikProvider>
      </Grid>
    </Grid>
  );
};
